import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Inbox, Loader2, Mail, MailOpen, Send, ArrowLeft, Trash2,
  CheckCheck, User, Clock, Reply, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getAuthToken } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DealerInbox = ({ isDark, onUnreadUpdate, filter = 'all' }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);

  // "platform" → only the shared-inbox aliases (advisor@, mike@).
  // "regular" → everything except platform-inbox mail.
  // "all"     → all inbox rows.
  const visibleMessages = React.useMemo(() => {
    if (filter === 'platform') return messages.filter(m => !!m.platform_inbox);
    if (filter === 'regular') return messages.filter(m => !m.platform_inbox);
    return messages;
  }, [messages, filter]);

  useEffect(() => {
    fetchInbox();
  }, []);

  const fetchInbox = async () => {
    try {
      setIsLoading(true);
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/inbox`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data);
      const unread = res.data.filter(m => !m.is_read).length;
      if (onUnreadUpdate) onUnreadUpdate(unread);
    } catch (err) {
      console.error('Error fetching inbox:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const openMessage = async (msg) => {
    setSelectedMessage(msg);
    setReplyText('');
    if (!msg.is_read) {
      try {
        const token = getAuthToken();
        await axios.put(`${API}/dealer/inbox/${msg.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMessages(prev => prev.map(m =>
          m.id === msg.id ? { ...m, is_read: true } : m
        ));
        if (onUnreadUpdate) {
          const newUnread = messages.filter(m => !m.is_read && m.id !== msg.id).length;
          onUnreadUpdate(newUnread);
        }
      } catch (err) {
        console.error('Error marking as read:', err);
      }
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedMessage || isSendingReply) return;
    setIsSendingReply(true);
    try {
      const token = getAuthToken();
      await axios.post(`${API}/dealer/inbox/${selectedMessage.id}/reply`,
        { body_html: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reply sent to ${selectedMessage.from_email}`);
      setReplyText('');
      setSelectedMessage(prev => ({ ...prev, replied: true }));
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setIsSendingReply(false);
    }
  };

  const deleteMessage = async (id) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/dealer/inbox/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(prev => prev.filter(m => m.id !== id));
      if (selectedMessage?.id === id) setSelectedMessage(null);
      toast.success('Message deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const markAllRead = async () => {
    try {
      const token = getAuthToken();
      await axios.put(`${API}/dealer/inbox/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      if (onUnreadUpdate) onUnreadUpdate(0);
      toast.success('All messages marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="inbox-loading">
        <Loader2 className="animate-spin text-[#004E86]" size={36} />
      </div>
    );
  }

  // Message Detail View
  if (selectedMessage) {
    return (
      <div data-testid="inbox-detail-view">
        {/* Detail Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => setSelectedMessage(null)} data-testid="inbox-back-btn">
            <ArrowLeft size={16} />
          </Button>
          <div className="flex-1 min-w-0">
            <h3 className={`text-lg font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {selectedMessage.subject || '(No Subject)'}
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              From: {selectedMessage.from_name || selectedMessage.from_email}
              {selectedMessage.from_name && ` <${selectedMessage.from_email}>`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {selectedMessage.replied && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                <Reply size={12} /> Replied
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deleteMessage(selectedMessage.id)}
              className="text-gray-400 hover:text-red-500"
              data-testid="delete-message-btn"
            >
              <Trash2 size={16} />
            </Button>
          </div>
        </div>

        {/* Message Metadata */}
        <div className={`flex items-center gap-4 text-xs mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <span className="flex items-center gap-1"><Clock size={12} /> {formatDate(selectedMessage.received_at || selectedMessage.created_at)}</span>
          {selectedMessage.lead_name && (
            <span className="flex items-center gap-1"><User size={12} /> Lead: {selectedMessage.lead_name}</span>
          )}
        </div>

        {/* Message Body — always rendered in a white "letter" card so the
            original email styling (typically dark text on white) reads
            cleanly regardless of the dashboard theme. */}
        <div className="rounded-xl p-5 mb-4 bg-white text-gray-900 border border-gray-200 shadow-inner overflow-x-auto">
          {selectedMessage.html_body ? (
            <div
              className="prose max-w-none text-sm"
              dangerouslySetInnerHTML={{ __html: selectedMessage.html_body }}
              data-testid="message-body-html"
            />
          ) : (
            <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700" data-testid="message-body-text">
              {selectedMessage.text_body || '(Empty message)'}
            </pre>
          )}
        </div>

        {/* Reply Section */}
        <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`} data-testid="reply-section">
          <p className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            Reply to {selectedMessage.from_name || selectedMessage.from_email}
          </p>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
            rows={4}
            className={`w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#004E86] mb-3 ${
              isDark ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-400' : 'border-gray-200'
            }`}
            data-testid="reply-input"
          />
          <div className="flex justify-end">
            <Button
              onClick={handleReply}
              disabled={!replyText.trim() || isSendingReply}
              className="bg-[#004E86] hover:bg-[#003d6b] text-white flex items-center gap-2"
              data-testid="send-reply-btn"
            >
              {isSendingReply ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Send Reply
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Inbox List View
  return (
    <div data-testid="inbox-list-view">
      {/* Inbox Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            {visibleMessages.length > 0 ? `${visibleMessages.length} message${visibleMessages.length !== 1 ? 's' : ''}` : 'No messages'}
          </h3>
          {visibleMessages.filter(m => !m.is_read).length > 0 && (
            <span className="text-xs bg-[#004E86] text-white rounded-full px-2 py-0.5" data-testid="unread-badge">
              {visibleMessages.filter(m => !m.is_read).length} unread
            </span>
          )}
        </div>
        {visibleMessages.some(m => !m.is_read) && (
          <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs" data-testid="mark-all-read-btn">
            <CheckCheck size={14} className="mr-1" /> Mark all read
          </Button>
        )}
      </div>

      {/* Empty State */}
      {visibleMessages.length === 0 ? (
        <div className={`text-center py-16 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`} data-testid="inbox-empty">
          <Inbox size={48} className={`mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`text-lg font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {filter === 'platform' ? 'No advisor / mike@ mail yet' : 'Your inbox is empty'}
          </p>
          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {filter === 'platform'
              ? 'Emails sent to advisor@rvmatchai.app or mike@rvmatchai.app will show up here.'
              : 'When leads reply to your emails, their responses will appear here.'}
          </p>
        </div>
      ) : (
        <div className={`rounded-xl shadow-sm overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
            {visibleMessages.map((msg) => (
              <button
                key={msg.id}
                onClick={() => openMessage(msg)}
                className={`w-full text-left p-4 flex items-start gap-3 transition-colors ${
                  !msg.is_read
                    ? isDark ? 'bg-[#004E86]/10 hover:bg-[#004E86]/20' : 'bg-blue-50/50 hover:bg-blue-50'
                    : isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                }`}
                data-testid={`inbox-message-${msg.id}`}
              >
                {/* Read/Unread Indicator */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                  !msg.is_read ? 'bg-[#004E86] text-white' : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400'
                }`}>
                  {!msg.is_read ? <Mail size={14} /> : <MailOpen size={14} />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-sm truncate ${!msg.is_read ? 'font-bold' : 'font-medium'} ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {msg.from_name || msg.from_email}
                    </span>
                    {msg.replied && (
                      <span className="text-xs text-green-600 flex items-center gap-0.5 shrink-0">
                        <Reply size={10} /> Replied
                      </span>
                    )}
                  </div>
                  <p className={`text-sm truncate mb-0.5 ${!msg.is_read ? 'font-semibold' : ''} ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {msg.subject || '(No Subject)'}
                  </p>
                  <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {(msg.text_body || '').substring(0, 120)}
                  </p>
                </div>

                {/* Date + Arrow */}
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {formatDate(msg.received_at || msg.created_at)}
                  </span>
                  <ChevronRight size={14} className="text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealerInbox;
